/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback } from "react";
import { useAppDispatch, useGetUserData, useUserReducer } from "../../Utility";
import { admin, cities as citiesList } from "../../config/appData";
import {
  onPostUsers,
  onPostUsersReset,
  onGetUsers,
} from "../../Store/Slices/userSlice";
import toast from "react-hot-toast";
import CommonTable from "../Components/CommonTable";
import "./BusinessUsersPage.css";
import "./CreateUserPage.css";
import { formatDate } from "../../utils/UtilityFunctions";

const PAGE_SIZE = 10;
const ROLE_SUGGESTIONS = ["Manager", "Executive", "Executive Manager"];
const FORBIDDEN_ROLES = ["user", "business"];

const initialForm = {
  name: "",
  phone: "",
  email: "",
  password: "",
  confirmPassword: "",
  role: "",
  cities: [],
  access_modules: [],
  is_active: true,
};

// Multiselect dropdown: options = [{ value, label }] or string[]; selected = value[]
function MultiselectDropdown({
  id,
  label,
  options,
  selected = [],
  onChange,
  placeholder = "Select...",
  error,
  disabled,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const opts = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggle = useCallback(
    (value) => {
      if (disabled) return;
      const next = selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value];
      onChange(next);
    },
    [selected, onChange, disabled]
  );

  const displayText =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? opts.find((o) => o.value === selected[0])?.label ?? selected[0]
        : `${selected.length} selected`;

  return (
    <div className={`admin-multiselect-dropdown ${className}`} ref={ref}>
      {label && (
        <label className="admin-form-label" htmlFor={id}>
          {label}
        </label>
      )}
      <button
        type="button"
        id={id}
        className={`admin-form-input admin-multiselect-trigger ${error ? "admin-form-input-error" : ""}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={selected.length === 0 ? "admin-multiselect-placeholder" : ""}>
          {displayText}
        </span>
        <span className="admin-multiselect-chevron" aria-hidden>
          ▼
        </span>
      </button>
      {open && (
        <div
          className="admin-multiselect-dropdown-list"
          role="listbox"
          aria-multiselectable="true"
        >
          {opts.map((opt) => (
            <div
              key={opt.value}
              role="option"
              aria-selected={selected.includes(opt.value)}
              className={`admin-multiselect-option ${selected.includes(opt.value) ? "selected" : ""}`}
              onClick={() => toggle(opt.value)}
            >
              <span className="admin-multiselect-option-check">{selected.includes(opt.value) ? "✓" : ""}</span>
              {opt.label}
            </div>
          ))}
        </div>
      )}
      {error && <span className="admin-form-error">{error}</span>}
    </div>
  );
}

function CreateUserPage() {
  const dispatch = useAppDispatch();
  const usersRaw = useGetUserData();
  const list = Array.isArray(usersRaw) ? usersRaw : [];
  const filteredList = list.filter((row) => {
    const r = (row.role || row.user_type || "").toLowerCase().trim();
    return !["user", "admin", "business"].includes(r);
  });
  const userReducer = useUserReducer();
  const loading = userReducer?.postUsersLoading ?? false;
  const listLoading = userReducer?.getUsersLoading ?? false;
  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [confirmModal, setConfirmModal] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const adminPages = admin?.menuItems ?? [];

  const pathToLabel = (path) => {
    const item = adminPages.find((m) => m.path === path || m.path === path?.trim());
    return item ? item.label : (path || "—");
  };

  const formatModulesWithLabels = (row) => {
    const raw = row.access_modules ?? row.modules;
    const toPaths = (v) => {
      if (Array.isArray(v)) return v.map(String).filter(Boolean);
      if (v == null) return [];
      const s = String(v).trim();
      if (!s) return [];
      return s.split(",").map((p) => p.trim()).filter(Boolean);
    };
    const paths = toPaths(raw);
    if (paths.length === 0) return "—";
    const labels = paths.map((p) => pathToLabel(p)).filter(Boolean);
    return labels.length ? labels.join(", ") : "—";
  };

  useEffect(() => {
    dispatch(onGetUsers());
  }, []);

  useEffect(() => {
    if (userReducer?.postUsersStatusCode === "201" || userReducer?.postUsersStatusCode === "200") {
      dispatch(onPostUsersReset());
      toast.success(userReducer?.postUsersMessage ?? "Success");
      setFormData(initialForm);
      setErrors({});
      setFormOpen(false);
      setEditRow(null);
      setConfirmModal(null);
      dispatch(onGetUsers());
    } else if (userReducer?.postUsersStatusCode === "400" || userReducer?.postUsersError) {
      dispatch(onPostUsersReset());
      toast.error(userReducer?.postUsersMessage ?? "Failed");
    } else if (userReducer?.postUsersStatusCode) {
      dispatch(onPostUsersReset());
      toast.error(userReducer?.postUsersMessage ?? "Failed");
    }
  }, [
    userReducer?.postUsersStatusCode,
    userReducer?.postUsersMessage,
    userReducer?.postUsersError,
  ]);

  const isEditMode = Boolean(editRow);

  const validate = () => {
    const e = {};
    if (!(formData.name || "").trim()) e.name = "Name is required";
    if (!(formData.phone || "").trim()) e.phone = "Phone number is required";
    if (!(formData.email || "").trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = "Invalid email";
    if (!isEditMode) {
      if (!(formData.password || "").trim()) e.password = "Password is required";
      else if ((formData.password || "").length < 6) e.password = "Password must be at least 6 characters";
      if (!(formData.confirmPassword || "").trim()) e.confirmPassword = "Confirm password is required";
      else if (formData.password !== formData.confirmPassword) e.confirmPassword = "Passwords do not match";
    } else if ((formData.password || "").trim() && formData.password.length < 6) {
      e.password = "Password must be at least 6 characters";
    }
    const roleVal = (formData.role || "").trim().toLowerCase();
    if (!(formData.role || "").trim()) e.role = "Role is required";
    else if (FORBIDDEN_ROLES.includes(roleVal)) e.role = "Role cannot be 'user' or 'business'. Use Manager, Executive, or Executive Manager.";
    if (!formData.cities?.length) e.cities = "Select at least one city";
    if (!formData.access_modules?.length) e.access_modules = "Select at least one access page";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "role") {
      const val = (value || "").trim().toLowerCase();
      if (FORBIDDEN_ROLES.includes(val)) {
        setErrors((prev) => ({ ...prev, role: "Role cannot be 'user' or 'business'. Use Manager, Executive, or Executive Manager." }));
        return;
      }
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[e.target.name]) setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = {
      name: formData.name.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      role: formData.role.trim(),
      cities: formData.cities,
      access_modules: formData.access_modules,
      admin_access:true,
      is_active: formData.is_active !== false,
    };
    if (isEditMode) {
      payload.id = editRow.id;
      if ((formData.password || "").trim()) payload.password = formData.password;
      dispatch(onPostUsers(payload));
    } else {
      payload.password = formData.password;
      dispatch(onPostUsers(payload));
    }
  };

  const closeForm = () => {
    if (!loading) {
      setFormOpen(false);
      setEditRow(null);
      setFormData(initialForm);
      setErrors({});
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  };

  const openFormForAdd = () => {
    setEditRow(null);
    setFormData(initialForm);
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
    setFormOpen(true);
  };

  const openFormForEdit = (row) => {
    const citiesRaw = row.cities ?? row.cities_allowed;
    const modulesRaw = row.access_modules ?? row.modules;
    const toArray = (v) => {
      if (Array.isArray(v)) return v.filter(Boolean).map(String);
      if (v == null) return [];
      const s = String(v).trim();
      if (!s) return [];
      if (s.includes(",")) return s.split(",").map((x) => x.trim()).filter(Boolean);
      return [s];
    };
    setEditRow(row);
    setFormData({
      name: row.name || row.business_name || "",
      phone: row.phone ?? "",
      email: row.email ?? "",
      password: "",
      confirmPassword: "",
      role: row.role || row.user_type || "",
      admin_access:true,
      cities: toArray(citiesRaw),
      access_modules: toArray(modulesRaw),
      is_active: row.is_active !== false,
    });
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
    setFormOpen(true);
  };

  // const openConfirm = (row, action) => {
  //   const name = row.name || row.business_name || row.email || `User #${row.id ?? "?"}`;
  //   setConfirmModal({
  //     id: row.id,
  //     name,
  //     action,
  //     isActive: row.is_active !== false,
  //   });
  // };

  const closeConfirm = () => {
    if (!loading) setConfirmModal(null);
  };

  const handleConfirmAction = () => {
    if (!confirmModal?.id) return;
    if (confirmModal.action === "delete") {
      dispatch(onPostUsers({ id: confirmModal.id, is_deleted: true }));
    } else {
      dispatch(onPostUsers({ id: confirmModal.id, is_active: !confirmModal.isActive }));
    }
    setConfirmModal(null);
  };

  const formatList = (val) => {
    if (Array.isArray(val)) return val.filter(Boolean).join(", ") || "—";
    if (val != null && String(val).trim()) return String(val).trim();
    return "—";
  };

  const cities = Array.isArray(citiesList) ? citiesList : [];

  const columns = [
    { key: "id", title: "ID", dataIndex: "id", render: (row) => row.id ?? "—" },
    {
      key: "name",
      title: "Name",
      render: (row) => (row.name || row.business_name) ?? "—",
    },
    {
      key: "email",
      title: "Email",
      dataIndex: "email",
      render: (row) => row.email ?? "—",
    },
    {
      key: "phone",
      title: "Phone",
      dataIndex: "phone",
      render: (row) => row.phone ?? "—",
    },
    {
      key: "role",
      title: "Type",
      render: (row) => {
        const r = row.role || row.user_type || "";
        if (r === "business") return "Business";
        if (r === "user") return "Normal";
        return r ? String(r).charAt(0).toUpperCase() + String(r).slice(1) : "—";
      },
    },
    {
      key: "modules",
      title: "Modules",
      render: (row) => formatModulesWithLabels(row),
    },
    {
      key: "cities_allowed",
      title: "Cities allowed",
      render: (row) => formatList(row.cities ?? row.cities_allowed),
    },
    {
      key: "is_active",
      title: "Status",
      render: (row) => (
        <span
          className={`admin-table-status admin-table-status-${row.is_active !== false ? "approved" : "rejected"}`}
        >
          {row.is_active !== false ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "created_at",
      title: "Registered",
      render: (row) =>
        row.created_at
          ? new Date(row.created_at).toLocaleDateString("en-IN", { dateStyle: "short" })
          : "—",
    },
    {
      key: "Last Login",
      title: "Last Login",
      render: (row) => row.last_login_at ? formatDate(row.last_login_at,true) : "—",
    },
    {
      key: "actions",
      title: "Actions",
      render: (row) => (
        <div className="admin-table-actions" style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button
            type="button"
            className="admin-btn-approve"
            onClick={() => openFormForEdit(row)}
            disabled={loading}
          >
            Update
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="admin-create-user">
      <div className="admin-page-header admin-create-user-header">
        <div>
          <h1 className="admin-page-title">Create User</h1>
          <p className="admin-page-subtitle">
            Add new users and manage the list below.
          </p>
        </div>
        <button
          type="button"
          className="admin-btn-add-user"
          onClick={openFormForAdd}
          disabled={listLoading}
        >
          Add User
        </button>
      </div>

      <CommonTable
        columns={columns}
        data={filteredList}
        loading={listLoading}
        emptyText="No users yet. Click Add User to create one."
        showSearch
        searchPlaceholder="Search all..."
        searchKeys={["id", "name", "business_name", "email", "phone", "access_modules", "cities"]}
        pageSize={PAGE_SIZE}
      />

      {formOpen && (
        <div
          className="admin-modal-overlay"
          onClick={closeForm}
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-user-modal-title"
        >
          <div
            className="admin-modal admin-modal-create-user admin-create-user-form-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-modal-create-user-header">
              <h2 id="create-user-modal-title" className="admin-create-business-form-title">
                {isEditMode ? "Update user" : "Add user"}
              </h2>
              <button
                type="button"
                className="admin-modal-close-btn"
                onClick={closeForm}
                disabled={loading}
                aria-label="Close"
              >
                <span aria-hidden>×</span>
              </button>
            </div>
            <form className="admin-data-form create-user-form" onSubmit={handleSubmit}>
              <div className="admin-modal-form-body">
                <div className="admin-form-section">
               
                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label htmlFor="create-user-name" className="admin-form-label">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="create-user-name"
                        name="name"
                        className={`admin-form-input ${errors.name ? "admin-form-input-error" : ""}`}
                        placeholder="Enter name"
                        value={formData.name}
                        onChange={handleChange}
                      />
                      {errors.name && <span className="admin-form-error">{errors.name}</span>}
                    </div>
                    <div className="admin-form-group">
                      <label htmlFor="create-user-phone" className="admin-form-label">
                        Phone number *
                      </label>
                      <input
                        type="tel"
                        id="create-user-phone"
                        name="phone"
                        className={`admin-form-input ${errors.phone ? "admin-form-input-error" : ""}`}
                        placeholder="Enter phone number"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                      {errors.phone && <span className="admin-form-error">{errors.phone}</span>}
                    </div>
                  </div>
                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label htmlFor="create-user-email" className="admin-form-label">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="create-user-email"
                        name="email"
                        className={`admin-form-input ${errors.email ? "admin-form-input-error" : ""}`}
                        placeholder="Enter email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                      {errors.email && <span className="admin-form-error">{errors.email}</span>}
                    </div>
                  </div>
                </div>

                <div className="admin-form-section">
       
                  <div className="admin-form-row">
                    <div className="admin-form-group admin-form-group-password">
                      <label htmlFor="create-user-password" className="admin-form-label">
                        {isEditMode ? "Password (leave blank to keep current)" : "Password *"}
                      </label>
                      <div className="admin-form-input-password-wrap">
                        <input
                          type={showPassword ? "text" : "password"}
                          id="create-user-password"
                          name="password"
                          className={`admin-form-input ${errors.password ? "admin-form-input-error" : ""}`}
                          placeholder={isEditMode ? "Leave blank to keep current" : "Min 6 characters"}
                          value={formData.password}
                          onChange={handleChange}
                        />
                        <button
                          type="button"
                          className="admin-form-password-toggle"
                          onClick={() => setShowPassword((p) => !p)}
                          tabIndex={-1}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          title={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                          )}
                        </button>
                      </div>
                      {errors.password && <span className="admin-form-error">{errors.password}</span>}
                    </div>
                    {!isEditMode && (
                      <div className="admin-form-group admin-form-group-password">
                        <label htmlFor="create-user-confirm-password" className="admin-form-label">
                          Confirm password *
                        </label>
                        <div className="admin-form-input-password-wrap">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            id="create-user-confirm-password"
                            name="confirmPassword"
                            className={`admin-form-input ${errors.confirmPassword ? "admin-form-input-error" : ""}`}
                            placeholder="Re-enter password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                          />
                          <button
                            type="button"
                            className="admin-form-password-toggle"
                            onClick={() => setShowConfirmPassword((p) => !p)}
                            tabIndex={-1}
                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            title={showConfirmPassword ? "Hide password" : "Show password"}
                          >
                            {showConfirmPassword ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                            ) : (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                            )}
                          </button>
                        </div>
                        {errors.confirmPassword && <span className="admin-form-error">{errors.confirmPassword}</span>}
                      </div>
                    )}
                  </div>
                </div>

                <div className="admin-form-section">
              
                  <div className="admin-form-group">
                    <label htmlFor="create-user-role" className="admin-form-label">
                      Role * (pick one below; cannot be user or business)
                    </label>
                    <input
                      type="text"
                      id="create-user-role"
                      name="role"
                      className={`admin-form-input ${errors.role ? "admin-form-input-error" : ""}`}
                      placeholder="e.g. Manager, Executive, Executive Manager"
                      value={formData.role}
                      onChange={handleChange}
                    />
                    {errors.role && <span className="admin-form-error">{errors.role}</span>}
                    <div className="admin-role-suggestions-wrap">
                      <span className="admin-role-suggestions-label">Click to set one role:</span>
                      <div className="admin-role-suggestions">
                        {ROLE_SUGGESTIONS.map((s) => (
                          <button
                            key={s}
                            type="button"
                            className={`admin-role-suggestion-item ${formData.role === s ? "active" : ""}`}
                            onClick={() => {
                              setFormData((prev) => ({ ...prev, role: s }));
                              if (errors.role) setErrors((prev) => ({ ...prev, role: "" }));
                            }}
                            disabled={loading}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="admin-form-section">

                  <div className="admin-form-row">
                    <MultiselectDropdown
                      id="create-user-cities"
                      label="City * (select at least one)"
                      options={cities}
                      selected={formData.cities}
                      onChange={(next) => {
                        setFormData((prev) => ({ ...prev, cities: next }));
                        if (errors.cities) setErrors((prev) => ({ ...prev, cities: "" }));
                      }}
                      placeholder="Select cities"
                      error={errors.cities}
                      disabled={loading}
                    />
                    <MultiselectDropdown
                      id="create-user-access"
                      label="Access role * (select at least one)"
                      options={adminPages.map((m) => ({ value: m.path, label: m.label }))}
                      selected={formData.access_modules}
                      onChange={(next) => {
                        setFormData((prev) => ({ ...prev, access_modules: next }));
                        if (errors.access_modules) setErrors((prev) => ({ ...prev, access_modules: "" }));
                      }}
                      placeholder="Select admin pages"
                      error={errors.access_modules}
                      disabled={loading}
                    />
                  </div>
                  {adminPages.length > 0 && (
                    <p className="admin-form-hint">Select which admin pages this user can access.</p>
                  )}
                </div>

                {isEditMode && editRow && (
                  <div className="admin-form-section admin-form-edit-actions">
                    <span className="admin-form-label">User status &amp; actions</span>
                    <div className="admin-form-edit-actions-row">
                      <span className="admin-form-edit-status">
                        Status:{" "}
                        <span className={`admin-table-status admin-table-status-${editRow.is_active !== false ? "approved" : "rejected"}`}>
                          {editRow.is_active !== false ? "Active" : "Inactive"}
                        </span>
                      </span>
                      <button
                        type="button"
                        className={editRow.is_active !== false ? "admin-btn-deactivate" : "admin-btn-approve"}
                        onClick={() => {
                          dispatch(onPostUsers({ id: editRow.id, is_active: !editRow.is_active }));
                          setFormOpen(false);
                          setEditRow(null);
                        }}
                        disabled={loading}
                      >
                        {editRow.is_active !== false ? "Make inactive" : "Make active"}
                      </button>
                      <button
                        type="button"
                        className="admin-btn-delete"
                        onClick={() => {
                          setConfirmModal({
                            id: editRow.id,
                            name: formData.name || editRow.email || `User #${editRow.id}`,
                            action: "delete",
                            isActive: editRow.is_active !== false,
                          });
                        }}
                        disabled={loading}
                      >
                        Delete user
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="admin-form-actions">
                <button
                  type="button"
                  className="admin-form-btn admin-form-btn-cancel"
                  onClick={closeForm}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-form-btn admin-form-btn-submit"
                  disabled={loading}
                >
                  {loading ? (isEditMode ? "Updating…" : "Creating…") : (isEditMode ? "Update user" : "Create user")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmModal && (
        <div
          className="admin-modal-overlay"
          onClick={closeConfirm}
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-user-confirm-title"
        >
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h2 id="create-user-confirm-title" className="admin-modal-title">
              {confirmModal.action === "delete"
                ? "Delete user?"
                : confirmModal.isActive
                  ? "Deactivate user?"
                  : "Activate user?"}
            </h2>
            <p className="admin-modal-text">
              {confirmModal.action === "delete" ? (
                <>Are you sure you want to delete <strong>{confirmModal.name}</strong>? This cannot be undone.</>
              ) : confirmModal.isActive ? (
                <>Deactivate <strong>{confirmModal.name}</strong>? They will not be able to log in until activated again.</>
              ) : (
                <>Activate <strong>{confirmModal.name}</strong>? They will be able to log in again.</>
              )}
            </p>
            <div className="admin-modal-actions">
              <button
                type="button"
                className="admin-modal-btn admin-modal-cancel"
                onClick={closeConfirm}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`admin-modal-btn ${confirmModal.action === "delete" ? "admin-modal-reject" : confirmModal.isActive ? "admin-modal-reject" : "admin-modal-confirm"}`}
                onClick={handleConfirmAction}
                disabled={loading}
              >
                {loading ? "Please wait…" : confirmModal.action === "delete" ? "Delete" : confirmModal.isActive ? "Deactivate" : "Activate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateUserPage;
